import React from 'react';
import { Box, BoxProps } from '@mui/material';
import { dataModelIcons } from '/src/components';
import { FuseSelect } from '/src/components';
import { Company, TCompanyId } from '/src/lib/models';
import { DataModelType } from '/src/lib/types';

export type CompanySelectProps = {
  /**
   * Selected Company ID
   */
  value: TCompanyId;
  /**
   * Sets the currently selected Company ID
   * @param value TCompanyId
   * @returns void
   */
  setValue: (value: TCompanyId) => void;
  /**
   * List of Companies to display in the dropdown
   */
  companies: Company[];
  /**
   * Optional Box component props
   */
  boxProps?: BoxProps;
  /**
   * Show a label for this component
   */
  showLabel?: boolean;
  /**
   * Element ID
   */
  id?: string;
  /**
   * Element name
   */
  name?: string;
  /**
   * Element class
   */
  className?: string;
  /**
   * Element color
   */
  color?: string;
  /**
   * Whether this component is disabled
   */
  disabled?: boolean;
  /**
   * If true, shows error if value is empty
   */
  required?: boolean;
  /**
   * Error to display if `required` is set to true
   */
  error?: string;
  /**
   * Text to display if no value is set
   */
  placeholder?: string;
  /**
   * Label for screen readers
   */
  ariaLabel?: string;
  /**
   * Whether to enable the placeholder as a selectable option
   */
  enablePlaceholderOption?: boolean;
  /**
   * Hides the Select's bottom border
   */
  disableUnderline?: boolean;
  /**
   * Removes the Select's margins
   */
  disableMargin?: boolean;
};

export const CompanySelect: React.FC<CompanySelectProps> = ({
  value,
  setValue,
  companies,
  boxProps,
  placeholder = 'Select Company',
  ariaLabel = 'Select Company',
  ...selectProps
}) => {
  return (
    <Box display="flex" justifyContent="center" alignItems="center" gap="6px" {...boxProps}>
      <Box component="span">{dataModelIcons[DataModelType.Company]}</Box>
      <FuseSelect
        value={value}
        updateValue={(value) => setValue(value)}
        placeholder={placeholder}
        ariaLabel={ariaLabel}
        options={companies.map((company) => ({ label: company.name, value: company._id }))}
        {...selectProps}
      />
    </Box>
  );
};
